/* eslint-disable react-refresh/only-export-components */
import { FunctionComponent, PropsWithChildren } from "react"
import { useTranslation } from "react-i18next"
import { uniqBy } from "es-toolkit"

import { useAgentState } from "@/agent-state"
import { useMicrophoneErrorModal } from "@/components/modals/MicrophoneErrorModal"
import { Button } from "@/components/ui/button"
import { ErrorCard } from "@/components/ui/card"
import { cn } from "@/helpers/classNames"
import { useI18n } from "@/helpers/i18n/use-translation"
import {
  type CallErrorMap,
  getVoiceCallErrorLabel,
  isCallErrorKind,
  type VoiceCallError,
} from "@/models/call-errors"

export const useErrorToast = () => {
  const { send } = useAgentState()

  const pushNetworkingError = (message: keyof CallErrorMap) => {
    send({
      type: "SET_NETWORKING_ERROR",
      error_kind: message,
    })
  }

  const resetNetworkingError = (message: keyof CallErrorMap) => {
    send({
      type: "RESET_NETWORKING_ERROR",
      error_kind: message,
    })
  }

  return { pushNetworkingError, resetNetworkingError }
}

const CallErrorCard: FunctionComponent<
  { error: keyof CallErrorMap; errorLabel: VoiceCallError } & PropsWithChildren
> = ({ children, error, errorLabel }) => {
  const { t } = useTranslation()
  const { resetNetworkingError } = useErrorToast()

  return (
    <ErrorCard
      heading={t(`callPanel.callErrorCard.${errorLabel}.title`)}
      onClose={() => resetNetworkingError(error)}
    >
      {children}
    </ErrorCard>
  )
}

const MicPermissionButton = () => {
  const { t } = useI18n()
  const { showMicrophoneErrorModal } = useMicrophoneErrorModal()

  return (
    <div>
      <Button variant="secondary" size="sm" onClick={showMicrophoneErrorModal}>
        {t("callPanel.callErrorCard.micPermissionError.button")}
      </Button>
    </div>
  )
}

const RefreshPageButton = () => {
  const { t } = useI18n()

  return (
    <div>
      <Button
        variant="destructive"
        size="sm"
        onClick={() => window.location.reload()}
      >
        {t("global.button.refreshPage")}
      </Button>
    </div>
  )
}

const CallErrorDescription = ({
  errorLabel,
}: {
  errorLabel: VoiceCallError
}) => {
  const { tMap } = useI18n()

  return (
    <div>
      {tMap(
        `callPanel.callErrorCard.${errorLabel}.description`,
        (descriptionText, i) =>
          Array.isArray(descriptionText) ? (
            <ul className="list-inside list-disc" key={i} data-unmask>
              {tMap(
                `callPanel.callErrorCard.${errorLabel}.description.${i}`,
                (descSubtext, j) => (
                  <li key={j}>{descSubtext}</li>
                ),
              )}
            </ul>
          ) : (
            <p key={i}>{descriptionText}</p>
          ),
      )}
    </div>
  )
}

export const CallErrorCardStack: FunctionComponent<{ className?: string }> = ({
  className,
}) => {
  const { state } = useAgentState()
  const errorPairs = state.context.networkingErrors
    .filter(isCallErrorKind)
    .map((error) => ({
      error,
      label: getVoiceCallErrorLabel(error),
    }))

  // filtered and more friendly network errors
  const errors = uniqBy(errorPairs, (err) => err.label)

  if (!errors.length) {
    return null
  }

  return (
    <div className={cn(className, "flex flex-col gap-y-2")}>
      {errors.map(({ error, label }, i) => (
        <CallErrorCard key={i} error={error} errorLabel={label}>
          <CallErrorDescription key={i} errorLabel={label} />
          {label === "micPermissionError" && <MicPermissionButton />}
          {label === "networkError" && <RefreshPageButton />}
        </CallErrorCard>
      ))}
    </div>
  )
}

export function CallReportSubmitError({ className }: { className?: string }) {
  const { t } = useTranslation()

  return (
    <ErrorCard
      className={className}
      heading={t("callPanel.callErrorCard.submitError.title")}
    >
      {t("callPanel.callErrorCard.submitError.description")}
    </ErrorCard>
  )
}
